import { Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ValidationResult, Validator } from "@lchemy/model/validation";
import { of as observableOf } from "rxjs";
import { catchError, finalize, startWith, takeUntil } from "rxjs/operators";

import { FORM_ERROR_FORMATTER_MAP } from "@lantern/app/common";
import { GrowlService } from "@lantern/app/core/utils/growl-service";
import { AlertModalService } from "@lantern/app/core/utils/modals/alert-modal.service";

import { GlobalAuthService } from "@local/app/core/global-auth";
import {
  SetPasswordCmd,
  recaptchaSetPasswordValidator
} from "@local/app/models";
import { LOGIN_ERROR_MAP } from "@local/app/pages/shared";

@Component({
  templateUrl: "./set-password.template.html",
  providers: [
    {
      provide: FORM_ERROR_FORMATTER_MAP,
      useValue: LOGIN_ERROR_MAP
    }
  ]
})
export class SetPasswordComponent implements OnInit, OnDestroy {
  loading = false;
  expired = false;
  redirect?: string;

  resetForm: FormGroup = this.formBuilder.group({
    email: "",
    password: "",
    passwordConfirm: "",
    resetToken: "",
    recaptchaResponse: ""
  });
  resetCmdValidator: Validator<SetPasswordCmd> = recaptchaSetPasswordValidator;
  validationResult: ValidationResult;
  forceShowErrors: boolean = false;

  constructor(
    private growl: GrowlService,
    private globalAuthService: GlobalAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private alertModalService: AlertModalService
  ) {}

  ngOnInit(): void {
    this.initFormValuesFromRouteParams();
    this.initValidation();
  }

  private onDestroyEmitter = new EventEmitter();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  setRecaptchaResponse(recaptchaResponse: string): void {
    this.resetForm.patchValue({ recaptchaResponse });
  }

  setRecaptchaWrapperClass(visible: boolean): void {
    this.recaptchaWrapperClass = visible ? "pv--4" : "";
  }
  recaptchaWrapperClass = "";

  async submitForm(): Promise<void> {
    if (this.loading) {
      return;
    }
    if (this.resetForm.invalid) {
      this.growl.open("formError");
      this.forceShowErrors = true;
      return;
    }

    await this.validate();
    if (!this.validationResult.isValid) {
      this.growl.open("formError");
      this.forceShowErrors = true;
      return;
    }
    this.callResetPassword();
  }

  private initFormValuesFromRouteParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((params) => {
        this.resetForm.patchValue({
          email: params.email || "",
          resetToken: params.resetToken
        });
        this.expired = Number(params.expiration) < Date.now();
        this.redirect = params.redirect;

        if (this.resetForm.value.resetToken == null) {
          this.router.navigateByUrl("/forgot-password");
        }
      });
  }

  private callResetPassword(): void {
    const { email, password, resetToken, recaptchaResponse } =
      this.resetForm.value;

    this.loading = true;
    this.globalAuthService
      .resetPassword(email, password, resetToken)
      .pipe(
        catchError((error) => observableOf({ success: false, error })),
        finalize(() => (this.loading = false))
      )
      .subscribe((result) => {
        if (!result.success) {
          const errorMessage = (result as any)?.error?.error?.message;
          if (
            errorMessage != null &&
            errorMessage.includes("Cannot reuse one of your last")
          ) {
            this.growl.danger(errorMessage);
            return;
          } else {
            this.growl.danger(
              "Failed to set password. This link may have already been used or has expired."
            );
            return;
          }
        }

        this.globalAuthService
          .loginWithPassword(email, password, recaptchaResponse)
          .subscribe(
            () => {
              this.alertModalService.open({
                title: "Check your email",
                message:
                  "We've sent a one-time login link to the email address associated with your account. Click the link in the email to log in."
              });
            },
            (e) => {
              if (
                e.status === 502 &&
                e.error.message.includes("Failed to send email")
              ) {
                this.growl.open({
                  type: "danger",
                  message:
                    "Failed to send magic link email. Please contact an administrator or try again later."
                });
              }
            }
          );
      });
  }

  private initValidation(): void {
    this.resetForm.valueChanges
      .pipe(startWith(true), takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.validate());
  }

  async validate(): Promise<void> {
    this.validationResult = await this.resetCmdValidator.validate(
      this.resetForm.value
    );
  }
}
